// Pure THREE-based geometry helpers (no DOM / viewer imports, so unit-testable).
import * as THREE from 'three';

/**
 * Is this point on the cut-away side of a clipping plane?
 *
 * three clips a fragment when it sits at a negative distance from the plane,
 * and the raycaster does not know about clipping at all - so every pick has to
 * ask this, or you measure the part of the model the section just removed.
 */
export function isPointClipped(point: THREE.Vector3, planes: readonly THREE.Plane[]): boolean {
  for (const plane of planes) {
    if (plane.distanceToPoint(point) < 0) return true;
  }
  return false;
}

/** Shortest distance between two finite segments; returns closest points. */
export function closestPointsOnSegments(
  a0: THREE.Vector3, a1: THREE.Vector3,
  b0: THREE.Vector3, b1: THREE.Vector3
): { dist: number; pa: THREE.Vector3; pb: THREE.Vector3 } {
  const d1 = a1.clone().sub(a0);
  const d2 = b1.clone().sub(b0);
  const r = a0.clone().sub(b0);
  const a = d1.dot(d1);
  const e = d2.dot(d2);
  const f = d2.dot(r);
  let s: number;
  let t: number;

  if (a <= 1e-14 && e <= 1e-14) {
    return { dist: a0.distanceTo(b0), pa: a0.clone(), pb: b0.clone() };
  }
  if (a <= 1e-14) {
    s = 0;
    t = Math.max(0, Math.min(1, f / e));
  } else {
    const c = d1.dot(r);
    if (e <= 1e-14) {
      t = 0;
      s = Math.max(0, Math.min(1, -c / a));
    } else {
      const b = d1.dot(d2);
      const denom = a * e - b * b;
      s = Math.abs(denom) < 1e-14 ? 0 : Math.max(0, Math.min(1, (b * f - c * e) / denom));
      t = (b * s + f) / e;
      if (t < 0) {
        t = 0;
        s = Math.max(0, Math.min(1, -c / a));
      } else if (t > 1) {
        t = 1;
        s = Math.max(0, Math.min(1, (b - c) / a));
      }
    }
  }

  const pa = a0.clone().addScaledVector(d1, s);
  const pb = b0.clone().addScaledVector(d2, t);
  return { dist: pa.distanceTo(pb), pa, pb };
}
